import { useCallback } from "react";
import type { PermissionedMutation } from "@/constants/permissions/permissions";
import useFeatureFlag from "../utilities/featureFlag/featureFlag";
import { entityHasMutationPermission } from "./entityHasMutationPermission";

/**
 * Returns a stable predicate `(permissions) => boolean` for a given mutation.
 * When the `INVESTOR_PORTAL_PERMISSIONING` flag is off the predicate always
 * returns `true`, so feature code does not have to repeat the flag plumbing.
 *
 * Safe to call inside async callbacks (e.g. mutation `onCompleted`) because
 * the returned function is `useCallback`-stable.
 */
export function useEntityMutationPermissionCheck(
	mutation: PermissionedMutation,
) {
	const isPermissionEnabled = useFeatureFlag("INVESTOR_PORTAL_PERMISSIONING");

	return useCallback(
		(permissions: readonly string[] | null | undefined) =>
			!isPermissionEnabled ||
			entityHasMutationPermission(permissions, mutation),
		[isPermissionEnabled, mutation],
	);
}
